import { useLazyQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { ResultOf, graphql } from '@eluve/graphql.tada';
import { useTenantName } from '@eluve/smart-blocks';

import { useAddUserToTenant } from '../hooks/useAddUserToTenant';
import { UserFragment } from '../operations';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_RESULTS_LIMIT = 25;

// Users not already members of the tenant (excluded members are still
// members), matched server-side against the search term.
const SearchAddableUsersQuery = graphql(
  `
    query SearchAddableUsers($filter: UsersBoolExp!, $limit: Int!) {
      users(
        where: $filter
        limit: $limit
        orderBy: [{ firstName: ASC }, { lastName: ASC }]
      ) {
        ...User
      }
    }
  `,
  [UserFragment],
);

type AddableUser = ResultOf<typeof UserFragment>;

interface AddUserActionProps {
  tenantId: string;
  onUserAdded?: () => void;
}

export const AddUserAction: React.FC<AddUserActionProps> = ({
  tenantId,
  onUserAdded,
}) => {
  const [selectedUser, setSelectedUser] = useState<AddableUser | undefined>();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<AddableUser[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const [open, setOpen] = useState(false);
  // Guards against out-of-order / post-reset responses: a resolved request
  // only writes its results if it belongs to the current input value.
  const latestQueryRef = useRef('');
  const toast = useToast();

  const [searchUsers, { loading }] = useLazyQuery(SearchAddableUsersQuery, {
    context: eluveAdminHasuraContext,
  });

  const addUserToTenant = useAddUserToTenant(tenantId);

  const tenantName = useTenantName(tenantId);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        const searchTerm = `%${query}%`;
        searchUsers({
          variables: {
            filter: {
              _and: [
                { _not: { tenants: { tenantId: { _eq: tenantId } } } },
                {
                  _or: [
                    { email: { _ilike: searchTerm } },
                    { firstName: { _ilike: searchTerm } },
                    { lastName: { _ilike: searchTerm } },
                    { displayName: { _ilike: searchTerm } },
                  ],
                },
              ],
            },
            limit: SEARCH_RESULTS_LIMIT,
          },
        })
          .then((result) => {
            if (latestQueryRef.current !== query) {
              return;
            }
            setSearchFailed(Boolean(result.error));
            setSearchResults(result.data?.users ?? []);
          })
          .catch(() => {
            if (latestQueryRef.current !== query) {
              return;
            }
            setSearchFailed(true);
          });
      }, 250),
    [searchUsers, tenantId],
  );

  useEffect(
    function cancelDebounceOnUnmount() {
      return () => debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  const handleSearchChange = (value: string) => {
    latestQueryRef.current = value;
    setSearch(value);
    setSearchResults([]);
    setSelectedUser(undefined);
    setSearchFailed(false);

    if (value.length < MIN_SEARCH_LENGTH) {
      debouncedSearch.cancel();
      return;
    }

    debouncedSearch(value);
  };

  const resetState = () => {
    debouncedSearch.cancel();
    latestQueryRef.current = '';
    setSearch('');
    setSearchResults([]);
    setSelectedUser(undefined);
    setSearchFailed(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetState();
    }
  };

  const handleAddUser = () => {
    if (!selectedUser) return;

    const name =
      selectedUser.displayName ??
      `${selectedUser.firstName} ${selectedUser.lastName}`;

    addUserToTenant({
      variables: {
        tenantId,
        userId: selectedUser.id,
        role: 'tenant-member',
      },
      onError: () => {
        toast.error(`Failed to add user ${name} to organization ${tenantName}`);
      },
    }).then(() => onUserAdded?.());

    handleOpenChange(false);
  };

  const emptyMessage = loading
    ? 'Searching users...'
    : search.length < MIN_SEARCH_LENGTH
      ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
      : searchFailed
        ? 'Search failed. Please try again.'
        : 'No users found';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondary">
          <Icon name="UserPlus" size="lg" className="mr-2" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <Stack direction="horizontal" gap={3} align="stretch">
          <Icon name="UserPlus" size="3xl" />
          <DialogHeader>
            <DialogTitle className="font-normal tracking-normal text-text-secondary">
              Add New user
            </DialogTitle>
            <DialogDescription>
              Organization: <strong>{tenantName}</strong>
            </DialogDescription>
          </DialogHeader>
        </Stack>

        <Separator className="my-2" />

        <Text>
          Search user by email or name to add them to the organization.
        </Text>
        <Command className="bg-bg-quaternary/60" shouldFilter={false}>
          <CommandInput
            placeholder="Search user"
            value={search}
            onValueChange={handleSearchChange}
          />
          <Separator />
          <CommandList>
            <CommandGroup heading="Users" className="h-[220px] overflow-auto">
              {searchResults.length === 0 && (
                <div className="px-2 py-3 text-center text-text-tertiary">
                  {emptyMessage}
                </div>
              )}
              {searchResults.map((user) => {
                const name =
                  user.displayName ?? `${user.firstName} ${user.lastName}`;
                return (
                  <CommandItem
                    className="text-text-secondary aria-selected:bg-border-secondary aria-selected:text-text-secondary data-checked:bg-border-accent-secondary"
                    key={user.id}
                    value={user.id}
                    onSelect={() => setSelectedUser(user)}
                    data-checked={
                      selectedUser?.id === user.id ? 'true' : undefined
                    }
                  >
                    <Stack
                      direction="horizontal"
                      align="center"
                      gap={3}
                      fullWidth={false}
                    >
                      {selectedUser?.id === user.id ? (
                        <Icon name="Check" size="xl" />
                      ) : (
                        <Icon name="User" size="xl" />
                      )}

                      <Text
                        weight="regular"
                        className="tracking-normal whitespace-pre-line"
                      >
                        {name}
                        <br />
                        <span className="text-text-tertiary">{user.email}</span>
                      </Text>
                    </Stack>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            type="button"
            disabled={!selectedUser}
          >
            <Icon name="Plus" size="lg" className="mr-2" />
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
